/**
 * Copyright © 2025 Delicious AI, LLC
 *
 * @author Bryan Sheranian <bryan.sheranian@deliciousai.com>
 */

import { isEqual } from 'lodash'
import { useCallback, useState } from 'react'

import { MercurySimpleProductFragment, ProductFragment } from '@/graphql'

export const useSelectionState = () => {
  const [selectedShelves, _setSelectedShelves] = useState<number[]>([])

  const [selectedItems, _setSelectedItems] = useState<
    Array<{
      rowIndex: number
      index: number
    }>
  >([])

  const [productToAdd, _setProductToAdd] = useState<
    ProductFragment | MercurySimpleProductFragment | null
  >(null)

  const clearSelections = useCallback(() => {
    _setProductToAdd(null)
    _setSelectedShelves([])
    _setSelectedItems([])
  }, [_setProductToAdd, _setSelectedShelves, _setSelectedItems])

  const clearSelectedShelvesAndItems = useCallback(() => {
    _setSelectedShelves([])
    _setSelectedItems([])
  }, [_setSelectedShelves, _setSelectedItems])

  const toggleSelectShelf = useCallback(
    (rowIndex: number) => {
      _setSelectedShelves(prev =>
        prev.includes(rowIndex)
          ? prev.filter(item => item !== rowIndex)
          : [...prev, rowIndex],
      )
      _setProductToAdd(null)
      _setSelectedItems([])
    },
    [_setSelectedShelves, _setProductToAdd, _setSelectedItems],
  )

  const toggleSelectItem = useCallback(
    ({ rowIndex, index }: { rowIndex: number; index: number }) => {
      _setSelectedItems(prev =>
        prev.some(item => isEqual(item, { rowIndex, index }))
          ? prev.filter(item => !isEqual(item, { rowIndex, index }))
          : [...prev, { rowIndex, index }],
      )
      _setSelectedShelves([])
      _setProductToAdd(null)
    },
    [_setSelectedItems, _setSelectedShelves, _setProductToAdd],
  )

  const setProductToAdd = useCallback(
    (product: ProductFragment | MercurySimpleProductFragment | null) => {
      _setProductToAdd(product)
      _setSelectedShelves([])
      _setSelectedItems([])
    },
    [_setProductToAdd, _setSelectedShelves, _setSelectedItems],
  )

  return {
    selectedShelves,
    selectedItems,
    setProductToAdd,
    clearSelections,
    clearSelectedShelvesAndItems,
    toggleSelectShelf,
    toggleSelectItem,
    productToAdd,
  }
}
